import React, { useState } from 'react';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/common/Button';
import * as FiIcons from 'react-icons/fi';

const { 
  FiImage, FiSliders, FiCrop, FiRotateCw, FiSun, FiDroplet, 
  FiFilter, FiUpload, FiSave, FiDownload, FiPlus, FiMinus,
  FiRefreshCw, FiLayers, FiSidebar, FiArrowLeft, FiArrowRight
} = FiIcons;

const PhotoEditor = () => {
  const [activeTab, setActiveTab] = useState('adjust');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showHistory, setShowHistory] = useState(false);
  
  const toolTabs = [
    { id: 'adjust', label: 'Adjust', icon: FiSliders },
    { id: 'crop', label: 'Crop', icon: FiCrop },
    { id: 'filters', label: 'Filters', icon: FiFilter },
    { id: 'effects', label: 'Effects', icon: FiDroplet },
    { id: 'text', label: 'Text', icon: FiIcons.FiType },
    { id: 'draw', label: 'Draw', icon: FiIcons.FiEdit2 }
  ];

  const adjustmentTools = [
    { label: 'Brightness', icon: FiSun, value: 0, min: -100, max: 100 },
    { label: 'Contrast', icon: FiIcons.FiSunrise, value: 0, min: -100, max: 100 },
    { label: 'Saturation', icon: FiDroplet, value: 0, min: -100, max: 100 },
    { label: 'Temperature', icon: FiIcons.FiThermometer, value: 0, min: -100, max: 100 },
    { label: 'Clarity', icon: FiIcons.FiStar, value: 0, min: -100, max: 100 },
    { label: 'Sharpness', icon: FiIcons.FiTriangle, value: 0, min: 0, max: 100 }
  ];

  const editorHistory = [
    { action: 'Brightness +15', timestamp: '2:34 PM' },
    { action: 'Contrast +10', timestamp: '2:33 PM' },
    { action: 'Crop Applied', timestamp: '2:30 PM' },
    { action: 'Image Uploaded', timestamp: '2:28 PM' }
  ];

  const filters = [
    { name: 'Original', preview: 'https://images.unsplash.com/photo-1541516160071-4bb0c5af65ba?w=80&h=80&fit=crop' },
    { name: 'Mono', preview: 'https://images.unsplash.com/photo-1541516160071-4bb0c5af65ba?w=80&h=80&fit=crop&auto=format&mono=1' },
    { name: 'Sepia', preview: 'https://images.unsplash.com/photo-1541516160071-4bb0c5af65ba?w=80&h=80&fit=crop&auto=format&sepia=80' },
    { name: 'Vintage', preview: 'https://images.unsplash.com/photo-1541516160071-4bb0c5af65ba?w=80&h=80&fit=crop&auto=format&contrast=-10&brightness=-10' },
    { name: 'Bright', preview: 'https://images.unsplash.com/photo-1541516160071-4bb0c5af65ba?w=80&h=80&fit=crop&auto=format&brightness=20' },
    { name: 'Cool', preview: 'https://images.unsplash.com/photo-1541516160071-4bb0c5af65ba?w=80&h=80&fit=crop&auto=format&saturation=-20&temperature=-20' }
  ];

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 50));
  };

  const renderToolPanel = () => {
    switch (activeTab) {
      case 'adjust':
        return (
          <div className="space-y-6">
            {adjustmentTools.map((tool) => (
              <div key={tool.label}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <SafeIcon icon={tool.icon} className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{tool.label}</span>
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{tool.value}</span>
                </div>
                <input
                  type="range"
                  min={tool.min}
                  max={tool.max}
                  value={tool.value}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  onChange={() => {}}
                />
              </div>
            ))}
          </div>
        );
      
      case 'filters':
        return (
          <div className="grid grid-cols-3 gap-3">
            {filters.map((filter) => (
              <div key={filter.name} className="text-center">
                <div className="aspect-square rounded-lg overflow-hidden mb-2">
                  <img 
                    src={filter.preview} 
                    alt={filter.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {filter.name}
                </span>
              </div>
            ))}
          </div>
        );
      
      case 'crop':
        return (
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Aspect Ratio
              </p>
              <div className="flex flex-wrap gap-2">
                {['Original', '1:1', '4:3', '16:9', '3:2', 'Custom'].map((ratio) => (
                  <button 
                    key={ratio} 
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {ratio}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Rotate & Flip
              </p>
              <div className="flex gap-2">
                <button className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <SafeIcon icon={FiRotateCw} className="w-5 h-5" />
                </button>
                <button className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <SafeIcon icon={FiIcons.FiRotateCcw} className="w-5 h-5" />
                </button>
                <button className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                </button>
                <button className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 dark:text-gray-400">
              {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tools coming soon
            </p>
          </div>
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Photo Editor
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Edit and enhance your photos with professional tools
            </p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" size="sm" icon={FiUpload}>
              Upload
            </Button>
            <Button variant="outline" size="sm" icon={FiSave}>
              Save
            </Button>
            <Button size="sm" icon={FiDownload}>
              Export
            </Button>
          </div>
        </div>

        {/* Editor Layout */}
        <div className="flex flex-1 gap-4 h-full">
          {/* Left Sidebar - Tools */}
          <div className="w-16 bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center py-4">
            {toolTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-12 h-12 mb-2 rounded-lg flex items-center justify-center ${
                  activeTab === tab.id
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={tab.label}
              >
                <SafeIcon icon={tab.icon} className="w-5 h-5" />
              </button>
            ))}
            
            <div className="flex-1" />
            
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`w-12 h-12 mb-2 rounded-lg flex items-center justify-center ${
                showHistory
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="History"
            >
              <SafeIcon icon={FiLayers} className="w-5 h-5" />
            </button>
            
            <button
              className="w-12 h-12 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Undo"
            >
              <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
            </button>
          </div>

          {/* Main Editor Area */}
          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center relative">
            <img 
              src="https://images.unsplash.com/photo-1541516160071-4bb0c5af65ba?w=800&h=600&fit=crop" 
              alt="Editing preview" 
              className="max-w-full max-h-full object-contain"
              style={{ transform: `scale(${zoomLevel / 100})` }}
            />
            
            {/* Zoom Controls */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white dark:bg-dark-800 rounded-full shadow-lg px-2 py-1 flex items-center space-x-1">
              <button
                onClick={handleZoomOut}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700"
                disabled={zoomLevel <= 50}
              >
                <SafeIcon icon={FiMinus} className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12 text-center">
                {zoomLevel}%
              </span>
              <button
                onClick={handleZoomIn}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700"
                disabled={zoomLevel >= 200}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Right Sidebar - Tool Options */}
          <motion.div 
            initial={{ width: 280 }}
            animate={{ width: showHistory ? 380 : 280 }}
            transition={{ duration: 0.3 }}
            className="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="flex h-full">
              {/* Tool Panel */}
              <div className="p-4 flex-1 overflow-y-auto">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Tools
                </h3>
                {renderToolPanel()}
              </div>
              
              {/* History Panel */}
              {showHistory && (
                <motion.div 
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 180 }}
                  exit={{ opacity: 0, width: 0 }}
                  className="border-l border-gray-200 dark:border-gray-700 p-4 overflow-y-auto"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                    History
                  </h3>
                  <div className="space-y-3">
                    {editorHistory.map((item, index) => (
                      <button 
                        key={index} 
                        className={`w-full text-left p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          index === 0 ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : ''
                        }`}
                      >
                        <div className="text-sm font-medium">
                          {item.action}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.timestamp}
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PhotoEditor;